import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NotificationIcon from "../helper/svg/notification.svg";
import { CiSearch } from "react-icons/ci";
import AdminIcon from "../helper/images/usericon.png";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUserById } from "../api/common/index";
import { getRestaurantsOrHotels } from "../api/user";
import { FiBell } from "react-icons/fi";
import { useState, useEffect, useRef } from "react";
import {
  getAllNotifications,
  markNotificationAsRead,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  deleteNotifications
} from "../api/common/notifications";
import moment from "moment";
import socket from "../helper/socket";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../context/LanguageContext";

const Header = () => {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const { data } = useQuery({
    queryKey: ["user"],
    queryFn: () => getUserById(),
  });
  const { data: userData } = data ?? {};

  // Search functionality
  const { data: searchResults, isLoading: isSearchLoading, error: searchError } = useQuery({
    queryKey: ["search-restaurants", debouncedQuery],
    queryFn: () => getRestaurantsOrHotels({
      hotels: false,
      search: debouncedQuery,
      limit: 5
    }),
    enabled: debouncedQuery.length > 2,
    keepPreviousData: true,
    retry: 1,
  });

  const restaurants = searchResults?.data?.data || [];

  useEffect(() => {
    if (!socket || !userData?._id) return;
    socket.emit("joinUserRoom", userData?._id);
  }, [socket, userData?._id]);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSearchResults(true);
  };

  // Handle search result selection
  const handleSearchSelect = (restaurant) => {
    setSearchQuery("");
    setShowSearchResults(false);
    setShowSearchResults(false);
    navigate(`/restaurantdetail`, {
      state: { restaurantId: restaurant._id },
    });
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    if (restaurants.length > 0) {
      setShowSearchResults(true);
    }
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchResults]);

  // Clear autofill / previously entered values on route change
  useEffect(() => {
    if (!searchInputRef.current) return;

    const input = searchInputRef.current;
    const clearAutofill = () => {
      if (input.value && input.value.includes("@")) {
        input.value = "";
      }
    };

    setSearchQuery("");
    input.value = "";
    const timers = [50, 150, 300, 600, 1000].map((delay) =>
      setTimeout(clearAutofill, delay)
    );

    const observer = new MutationObserver(clearAutofill);
    observer.observe(input, {
      attributes: true,
      attributeFilter: ["value", "autocomplete"],
    });

    const handleAutoInput = (e) => {
      if (
        e.inputType === undefined ||
        e.inputType === "insertReplacementText" ||
        e.inputType === "insertFromPaste"
      ) {
        if (e.target.value && e.target.value.includes("@")) {
          e.target.value = "";
          setSearchQuery("");
        }
      }
    };

    input.addEventListener("input", handleAutoInput);

    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
      input.removeEventListener("input", handleAutoInput);
    };
  }, [location.pathname]);

  const languageChangeHndle = (type) => {
    setLanguage(type);
  };
  return (
    <>
      <div className="grid grid-cols-[1fr_auto] justify-between bg-white header-shaow py-4 lg:px-[30px] sm:px-[20px] px-[15px] items-center sticky top-0 z-10">
        <h2 className="lg:text-[22px] text-base  md:pl-0 pl-[40px] font-semibold text-[#18181B]">
          {t("header").hello}, {userData?.name} !
        </h2>

        <div className="flex items-center lg:gap-[25px] gap-[18px] justify-end">
          <div className="relative w-full max-w-[301px] lg:block hidden" ref={searchRef}>
            {/* Hidden dummy field to stop browser autofill */}
            <input
              type="email"
              autoComplete="username"
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                opacity: 0,
                pointerEvents: "none",
                width: 0,
                height: 0,
                top: "-9999px",
                left: "-9999px",
              }}
              readOnly
            />
            <span className="absolute inset-y-0 left-3 flex items-center">
              <CiSearch size={24} className="text-[#4CAF50]" />
            </span>
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-1p-ignore="true"
              inputMode="search"
              name="dashboard_search"
              aria-label="Search"
              placeholder={t("header").searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 rounded-full border border-[#DDDDDD] focus:outline-none focus:border-[#4CAF50]"
            />
            
            {/* Search Results Dropdown */}
            {showSearchResults && (searchQuery.length > 2) && (
              <div className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-b-md z-50 max-h-[300px] overflow-auto mt-1 border border-gray-200">
                {isSearchLoading ? (
                  <div className="p-4 text-center">
                    <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Searching...</p>
                  </div>
                ) : searchError ? (
                  <div className="p-4 text-center text-red-500 text-sm">
                    Search failed. Please try again.
                  </div>
                ) : restaurants.length > 0 ? (
                  restaurants.map((restaurant) => (
                    <div
                      key={restaurant._id}
                      onClick={() => handleSearchSelect(restaurant)}
                      className="flex gap-3 items-center p-3 cursor-pointer hover:bg-gray-100 border-b border-gray-100"
                    >
                      <img
                        src={restaurant.images?.[0] || "/placeholder-restaurant.jpg"}
                        alt={restaurant.name}
                        className="w-12 h-12 object-cover rounded-md"
                        onError={(e) => {
                          e.target.src = "/placeholder-restaurant.jpg";
                        }}
                      />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-800">
                          {restaurant.name}
                        </h4>
                        <p className="text-xs text-gray-500">{restaurant.address}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-xs text-[#4CAF50]">⭐ {restaurant.rating || 'N/A'}</span>
                          {restaurant.distance && (
                            <span className="text-xs text-gray-400">
                              • {restaurant.distance} km
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No restaurants found
                  </div>
                )}
              </div>
            )}
          </div>
          <select
            value={language}
            onChange={(e) => {
              languageChangeHndle(e.target.value);
            }}
            className="w-full max-w-[120px] focus-visible:outline-none py-2 px-3 rounded-[5px] border border-[#DDDDDD] lg:block hidden"
          >
            <option value="English">English</option>
            <option value="Georgian">Georgian</option>
            <option value="Russian">Russian</option>
            {/* add more as needed */}
          </select>
          <NotificationDropdown></NotificationDropdown>
          <ProfileDropdown userData={userData} />
        </div>
      </div>
    </>
  );
};

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState(10); // start with 10
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1)

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["notifications", limit],
    queryFn: () => getAllNotifications({ limit, page }),
  });



  const { data: unreadCountData, refetch: refetchUnreadCount } = useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: getUnreadNotificationCount,
  });

  const { mutate: deleteAllNotifications } = useMutation({
    mutationFn: deleteNotifications,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notifications-unread-count"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("notifications").error;
      toast.error(errorMessage);
    },
  });

  const notifications = data?.data?.notifications || [];
  const totalCount = data?.data?.pagination?.total || 0;
  const unreadCount = unreadCountData?.data?.count || 0;

  useEffect(() => {
    const handleNewNotification = () => {
      refetch();
      refetchUnreadCount();
    };

    socket.on("receiveNotification", handleNewNotification);

    return () => {
      socket.off("receiveNotification", handleNewNotification);
    };
  }, [refetch, refetchUnreadCount]);

  useEffect(() => {
    if (open) refetch();

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, refetch]);

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      refetch();
      refetchUnreadCount();
    } catch (err) {
      console.error(t("header").errorMarkAsRead, err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      refetch();
      refetchUnreadCount();
    } catch (err) {
      console.error(t("header").errorMarkAsRead, err);
    }
  };

  const handleClearAll = async () => {

    deleteAllNotifications()

    
  };

  const handleLoadMore = () => {

    if (totalCount > limit) {
      setLimit((prev) => prev + 10);
    } else {
      setHasMore(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="cursor-pointer relative"
      >
        <FiBell className="text-black lg:w-[30px] w-[22px] h-auto" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
            {unreadCount}
          </span>
        )}
      </div>

      {open && (
        <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[72px] sm:top-auto mt-2 sm:mt-2 w-[calc(100vw-32px)] sm:w-80 max-w-[420px] sm:max-w-none max-h-[70vh] sm:max-h-[400px] overflow-auto bg-white shadow-lg border border-gray-200 rounded-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b font-medium">
            <span>{t("header").notifications}</span>
            <div className="flex gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {t("header").markAllAsRead}
                </button>
              )}
              {/* {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-sm text-red-600 hover:underline"
                >
                  {t("header").clearAll}
                </button>
              )} */}
            </div>
          </div>

          {isLoading ? (
            <div className="p-4 text-sm text-gray-500">
              {t("header").loading}...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-gray-500 text-sm">
              {t("header").noNotifications}
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n._id}
                className={`flex justify-between items-start px-4 py-3 border-b hover:bg-gray-50 cursor-pointer ${!n.read ? "bg-gray-100" : ""
                  }`}
                onClick={() => handleMarkAsRead(n._id)}
              >
                <div className="text-sm text-gray-800">
                  {n.message}
                  <div className="text-xs text-gray-500 mt-1">
                    {moment(n.createdAt).fromNow()}
                  </div>
                </div>
                {!n.isRead && (
                  <span className="ml-2 mt-1 bg-red-500 w-2 h-2 rounded-full flex-shrink-0"></span>
                )}
              </div>
            ))
          )}


          {notifications.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 text-sm border-t">

              <span
                className="text-red-600 hover:underline cursor-pointer"
                onClick={handleClearAll}
              >
                {t("header").clearAll}
              </span>

              {/* Show View More only if hasMore */}
              {hasMore ? (
                <span
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={handleLoadMore}
                >
                  {t("header").viewMore}
                </span>
              ) : (
                <span /> // keeps spacing if no "View More"
              )}

              {/* Always show Clear All */}

            </div>
          )}


          {/* {hasMore && notifications.length > 0 && (
            <div
              className="px-4 py-4 text-sm text-center text-blue-600 hover:underline cursor-pointer"
              onClick={handleLoadMore}
            >
              {t("header").viewMore}
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

const ProfileDropdown = ({ userData }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: t("header").logoutConfirmTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("header").logoutConfirmYes,
      cancelButtonText: t("header").logoutConfirmNo,
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        navigate("/");
      }
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="cursor-pointer relative"
      >
        <div className="w-[50px] h-[50px] rounded-full">
          <img
            src={
              userData?.profilePicture
                ? `${userData?.profilePicture}`
                : AdminIcon
            }
            alt="Profile"
            className="w-full max-w-[50px] h-[50px] rounded-full"
          />
        </div>
      </div>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg border border-gray-200 rounded-lg z-50">
          <div className="flex flex-col items-center justify-center px-4 py-3 border-b">
            <div className="w-[50px] h-[50px] rounded-full mb-2">
              <img
                src={
                  userData?.profilePicture
                    ? `${userData?.profilePicture}`
                    : AdminIcon
                }
                alt="Profile"
                className="w-full h-full rounded-full"
              />
            </div>
            <span className="font-medium text-lg">{userData?.name}</span>
            <p className="text-sm text-gray-500">{userData?.email}</p>

            {/* {userData?.role === "user" ? null : ( */}
              <p className="text-sm text-gray-500">
                {t("header").walletBalance}: ₾ {userData?.balance?.toFixed(2)}
              </p>
            {/* )} */}
          </div>

          <div className="p-3">
            <button
              onClick={handleLogout}
              className="w-full bg-[#209b25] hover:bg-[#209b25] text-white font-medium py-2 px-4 rounded-lg text-sm"
            >
              {t("header").logout}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
